import { type ChangeEvent, useRef, useState } from 'react'

import {
  Divider,
  Field,
  Flex,
  Form,
  IconDelete,
  IconUpload,
  PrimaryButton,
  SecondaryButton,
  Space,
  TertiaryButton,
  Text,
  TransparentIconButton,
} from '@procurify/ui'
import { FormikProvider, useFormik } from 'formik'
import moment, { type Moment } from 'moment'
import * as Yup from 'yup'

import { FormattedShortDate } from '@webapp/core/components/FormattedDate'
import { useProcurifyIntl } from '@webapp/hooks'
import { type CreditCardAccount } from '@webapp/modules/accounts-payable/entities/CreditCardAccount'
import messages from './messages'

const ACCEPTED_FILE_EXTENSIONS = /\.(qif|csv|xls)$/i
const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024
const MAX_ENDING_BALANCE = 999999999.99
const ENDING_BALANCE_PATTERN = /^\d+(\.\d{1,2})?$/

export interface ReconcileAccountFormSchema {
  currentStatementDate: Nullable<Moment>
  endingBalance: string
  /** Kept for Angular UI parity — not part of the session payload */
  importByStatementDate: boolean
  attachmentFile: Nullable<File>
}

export interface IReconcileAccountFormProps {
  /** Last-statement-date display + session reuse context */
  account: CreditCardAccount
  /** Match-screen variant — uploader section only (BHV-029) */
  importOnly: boolean
  isSaving: boolean
  onSubmit: (values: ReconcileAccountFormSchema) => void
  onCancel: () => void
}

// Form content only — the "Reconcile account" ModalDialog wrapper is FS-owned.
export const ReconcileAccountForm = ({
  account,
  importOnly,
  isSaving,
  onSubmit,
  onCancel,
}: IReconcileAccountFormProps) => {
  const intl = useProcurifyIntl()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [fileTypeError, setFileTypeError] = useState(false)
  const [fileSizeError, setFileSizeError] = useState(false)

  const validationSchema = Yup.object({
    currentStatementDate: importOnly
      ? Yup.mixed().nullable()
      : Yup.mixed()
          .nullable()
          .required(
            intl.formatMessage(messages.currentStatementDateRequiredError)
          ),
    // Optional; max 999999999.99 with 2 decimal places (BHV-026).
    endingBalance: Yup.string().test(
      'valid-balance',
      intl.formatMessage(messages.endingBalanceInvalidError),
      (value) =>
        !value ||
        (ENDING_BALANCE_PATTERN.test(value) &&
          parseFloat(value) <= MAX_ENDING_BALANCE)
    ),
  })

  const formik = useFormik<ReconcileAccountFormSchema>({
    initialValues: {
      // Defaults to today (BHV-026).
      currentStatementDate: importOnly ? null : moment(),
      endingBalance: '',
      importByStatementDate: false,
      attachmentFile: null,
    },
    validationSchema,
    onSubmit: (submittedValues, { setSubmitting }) => {
      onSubmit(submittedValues)
      setSubmitting(false)
    },
  })

  const { values, setFieldValue } = formik

  const handleFileSelected = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null
    event.target.value = ''

    if (!file) return

    // Reject non-.qif/.csv/.xls uploads (BHV-027). CSV importer-wizard
    // integration is HLD Open Question 1 — v1 ships attachment-upload-only.
    // TODO(HLD Open Question 1): launch the CSV importer wizard for .csv
    // files once the importer contract is verified.
    if (!ACCEPTED_FILE_EXTENSIONS.test(file.name)) {
      setFileTypeError(true)
      setFileSizeError(false)
      return
    }

    // Enforce the displayed 25.0 MB maximum (BHV-026).
    if (file.size > MAX_FILE_SIZE_BYTES) {
      setFileTypeError(false)
      setFileSizeError(true)
      return
    }

    setFileTypeError(false)
    setFileSizeError(false)
    setFieldValue('attachmentFile', file)
  }

  return (
    <FormikProvider value={formik}>
      <Form.Content noValidate autoComplete='off'>
        <Space size='large'>
          {!importOnly && (
            <>
              <Flex.Row>
                <Flex.Item width={1 / 3}>
                  <Field
                    label={intl.formatMessage(messages.lastStatementDateLabel)}
                  >
                    <Text autoMargin={false}>
                      {account.lastReconciliationDate ? (
                        <FormattedShortDate
                          date={account.lastReconciliationDate}
                        />
                      ) : (
                        '--'
                      )}
                    </Text>
                  </Field>
                </Flex.Item>
                <Flex.Item width={1 / 3}>
                  <Form.SingleDatePicker
                    id='currentStatementDate'
                    name='currentStatementDate'
                    label={intl.formatMessage(
                      messages.currentStatementDateLabel
                    )}
                    required
                    fullWidth
                    // Statement dates cannot be in the future (Angular
                    // max = today); past dates are selectable.
                    isOutsideRange={(day) => day.isAfter(moment(), 'day')}
                  />
                </Flex.Item>
                <Flex.Item width={1 / 3}>
                  <Form.TextField
                    id='endingBalance'
                    name='endingBalance'
                    label={intl.formatMessage(messages.endingBalanceLabel)}
                    placeholder={intl.formatMessage(
                      messages.endingBalancePlaceholder
                    )}
                  />
                </Flex.Item>
              </Flex.Row>
              <Divider />
            </>
          )}

          {/* Attach statement uploader — .qif/.csv/.xls only (BHV-027) */}
          <Space size='small'>
            <Text bold autoMargin={false}>
              {importOnly
                ? intl.formatMessage(messages.attachStatementTitle)
                : intl.formatMessage(messages.attachStatementOptionalTitle)}
            </Text>
            <Space direction='horizontal' size='small' align='center'>
              <input
                ref={fileInputRef}
                type='file'
                accept='.qif,.csv,.xls'
                hidden
                aria-hidden
                onChange={handleFileSelected}
                data-testid='reconcile-account-form-file-input'
              />
              <SecondaryButton
                type='button'
                iconComponent={<IconUpload />}
                onClick={() => fileInputRef.current?.click()}
              >
                {intl.formatMessage(messages.browseUploadButton)}
              </SecondaryButton>
              {values.attachmentFile ? (
                <Space direction='horizontal' size='small' align='center'>
                  <Text autoMargin={false}>{values.attachmentFile.name}</Text>
                  <TransparentIconButton
                    iconComponent={<IconDelete />}
                    type='button'
                    aria-label={intl.formatMessage(
                      messages.removeAttachmentAriaLabel
                    )}
                    onClick={() => setFieldValue('attachmentFile', null)}
                  />
                </Space>
              ) : (
                <Text color='textSecondary' autoMargin={false}>
                  {intl.formatMessage(messages.noFilesAttachedText)}
                </Text>
              )}
            </Space>
            {fileTypeError && (
              <Text color='alert' autoMargin={false}>
                {intl.formatMessage(messages.invalidFileTypeError)}
              </Text>
            )}
            {fileSizeError && (
              <Text color='alert' autoMargin={false}>
                {intl.formatMessage(messages.fileTooLargeError)}
              </Text>
            )}
          </Space>

          {!importOnly && (
            <>
              <Divider />
              <Form.Checkbox
                id='importByStatementDate'
                name='importByStatementDate'
              >
                {intl.formatMessage(messages.importByStatementDateCheckbox)}
              </Form.Checkbox>
            </>
          )}

          <Divider />
          <Space direction='horizontal' justify='end' align='center'>
            <TertiaryButton
              type='button'
              onClick={onCancel}
              disabled={isSaving}
            >
              {intl.formatMessage(messages.cancelButton)}
            </TertiaryButton>
            {/* Disabled while the statement is uploading (BHV-029) */}
            <PrimaryButton type='submit' isLoading={isSaving}>
              {importOnly
                ? intl.formatMessage(messages.importStatementButton)
                : intl.formatMessage(messages.beginMatchingButton)}
            </PrimaryButton>
          </Space>
        </Space>
      </Form.Content>
    </FormikProvider>
  )
}

ReconcileAccountForm.displayName = 'ReconcileAccountForm'
